/**
 * Keyboard contract for multi-field modal forms (Enter must not submit; date Enter commits).
 */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export type ModalFormEnterKeyMode = 'prevent-submit' | 'allow-submit';

/** One defer per Escape keydown when multiple modal-form listeners are registered. */
const deferredModalFormEscapeEvents = new WeakSet<KeyboardEvent>();

function isTextareaElement(target: EventTarget | null): boolean {
  return target instanceof HTMLTextAreaElement;
}

function isExplicitSubmitControl(target: EventTarget | null): boolean {
  if (target instanceof HTMLButtonElement) {
    return target.type === 'submit';
  }
  return false;
}

/**
 * True when Enter should commit a Mantine combobox/select option, not be swallowed by the form shell (#1405).
 */
function isComboboxKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  // Mantine searchable Select: never swallow Enter on the form bubble — Mantine commits on the
  // input target phase and may clear filter text before the event bubbles (#1405).
  if (target instanceof HTMLInputElement && target.classList.contains('mantine-Select-input')) {
    return true;
  }

  if (
    target.closest('[data-combobox-dropdown]')
    || target.closest('[data-mantine-combobox]')
    || target.getAttribute('role') === 'option'
  ) {
    return true;
  }

  const combobox =
    target.getAttribute('role') === 'combobox' ? target : target.closest('[role="combobox"]');
  if (!combobox) {
    return false;
  }

  if (combobox.getAttribute('aria-expanded') === 'true') {
    return true;
  }

  // Mantine Select can keep a portal listbox painted while aria-expanded is already false (#1405 mobile).
  if (typeof document !== 'undefined') {
    const controlsId = combobox.getAttribute('aria-controls');
    if (controlsId) {
      const ownedListbox = document.getElementById(controlsId);
      if (ownedListbox?.getAttribute('role') === 'listbox' && isVisibleOverlayElement(ownedListbox)) {
        return true;
      }
    }
    for (const listbox of document.querySelectorAll('[role="listbox"]')) {
      if (isVisibleOverlayElement(listbox)) {
        return true;
      }
    }
  }

  // Mantine Select closes the listbox before keyup; Enter must still commit typed filter text (#1405).
  if (combobox instanceof HTMLInputElement && combobox.value.trim()) {
    if (combobox.classList.contains('mantine-Select-input')) {
      return true;
    }
  }

  return false;
}

function isDateInputElement(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return Boolean(
    target.closest('[data-dates-input]')
    || target.closest('.mantine-DateInput-input'),
  );
}

/**
 * Returns true when Enter in a modal form should NOT trigger form submit.
 */
export function shouldPreventEnterFormSubmit(
  event: ReactKeyboardEvent<HTMLFormElement>,
  mode: ModalFormEnterKeyMode,
): boolean {
  if (mode === 'allow-submit') {
    return false;
  }
  if (event.key !== 'Enter' || event.defaultPrevented) {
    return false;
  }
  if (event.nativeEvent.isComposing) {
    return false;
  }
  const target = event.target;
  if (isTextareaElement(target)) {
    return false;
  }
  if (isExplicitSubmitControl(target)) {
    return false;
  }
  if (isComboboxKeyboardTarget(target)) {
    return false;
  }
  return true;
}

/** True when an overlay is painted and interactable (popover, calendar, etc.). */
export function isVisibleOverlayElement(element: Element): boolean {
  const html = element as HTMLElement;
  if (html.hasAttribute('hidden') || html.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  const style = window.getComputedStyle(html);
  if (
    style.display === 'none'
    || style.visibility === 'hidden'
    || style.opacity === '0'
    || style.pointerEvents === 'none'
  ) {
    return false;
  }
  const rect = html.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Mantine modal enter transitions can briefly paint the dialog shell at
 * `opacity: 0` before the fade completes. Visibility checks (including dialog
 * lists for Escape routing) use {@link isVisibleOverlayElement}; focus-based
 * routing still targets the dialog that owns the keydown when it is visible.
 */

/** Open overlay layer for keyboard deferral — ignores opacity:0 enter animations (#1447 mobile). */
export function isPaintedOverlayElement(element: Element): boolean {
  const html = element as HTMLElement;
  if (html.hasAttribute('hidden') || html.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  const style = window.getComputedStyle(html);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }
  const rect = html.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/** Visible modal dialogs in DOM order (Mantine portals each as a sibling). */
export function getVisibleDialogElements(): HTMLElement[] {
  if (typeof document === 'undefined') {
    return [];
  }
  return Array.from(document.querySelectorAll('[role="dialog"]')).filter((element) =>
    isVisibleOverlayElement(element),
  ) as HTMLElement[];
}

function getEffectiveStackingZIndex(element: HTMLElement): number {
  let max = 0;
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.position !== 'static') {
      const parsed = Number.parseInt(style.zIndex, 10);
      if (!Number.isNaN(parsed)) {
        max = Math.max(max, parsed);
      }
    }
    current = current.parentElement;
  }
  return max;
}

/** Topmost visible dialog, or null when none. */
export function getTopmostVisibleDialog(): HTMLElement | null {
  const dialogs = getVisibleDialogElements();
  if (dialogs.length === 0) {
    return null;
  }

  const active = document.activeElement;
  if (active instanceof Element) {
    const focusedDialog = active.closest('[role="dialog"]');
    if (focusedDialog instanceof HTMLElement && isVisibleOverlayElement(focusedDialog)) {
      return focusedDialog;
    }
  }

  let topmost = dialogs[0];
  let topZ = getEffectiveStackingZIndex(topmost);
  for (let index = 1; index < dialogs.length; index += 1) {
    const candidate = dialogs[index];
    const candidateZ = getEffectiveStackingZIndex(candidate);
    // Prefer later DOM siblings when z-index ties (nested fullscreen modals on mobile).
    if (candidateZ >= topZ) {
      topmost = candidate;
      topZ = candidateZ;
    }
  }
  return topmost;
}

/** Escape routing: prefer the dialog that owns the keydown target (nested modals on mobile). */
export function getTopmostVisibleDialogForKeyboardEvent(
  eventTarget: EventTarget | null,
): HTMLElement | null {
  if (eventTarget instanceof Element) {
    const targetDialog = eventTarget.closest('[role="dialog"]');
    if (targetDialog instanceof HTMLElement && isVisibleOverlayElement(targetDialog)) {
      return targetDialog;
    }
  }
  return getTopmostVisibleDialog();
}

/**
 * True when Escape should dismiss an inner layer (calendar, dropdown, nested dialog)
 * instead of routing to guarded modal close (#1432 regression after PR #867).
 */
export function shouldDeferModalFormEscape(
  event: ReactKeyboardEvent<HTMLElement>,
): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented) {
    return false;
  }
  return hasOpenModalInnerOverlay(event.target);
}

/** True when a Mantine DateInput calendar portal is on screen (used by popover Escape deferral). */
export function hasVisiblePortaledDatesPanel(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return Array.from(document.querySelectorAll('[data-dates-dropdown], [data-dates-modal]')).some(
    isPaintedOverlayElement,
  );
}

/**
 * Escape keydowns that must dismiss only the portaled Mantine calendar layer.
 * Primed at the start of document capture so listeners registered later in the
 * same phase (e.g. Set date popover) do not treat calendar-dismiss as popover-close (#1447).
 */
const escapeReservedForPortaledDatesPanel = new WeakSet<KeyboardEvent>();

function primeEscapeForVisiblePortaledDatesPanel(event: KeyboardEvent): void {
  if (event.key !== 'Escape') {
    return;
  }
  if (hasVisiblePortaledDatesPanel()) {
    escapeReservedForPortaledDatesPanel.add(event);
  }
}

/** True while a portaled calendar is still painted (popover must stay open). */
export function shouldSuppressPopoverEscapeClose(): boolean {
  return hasVisiblePortaledDatesPanel();
}

if (typeof document !== 'undefined') {
  document.addEventListener('keydown', primeEscapeForVisiblePortaledDatesPanel, true);
}

/** True when this Escape was reserved to close a portaled dates panel only. */
export function wasEscapeReservedForPortaledDatesPanel(event: KeyboardEvent): boolean {
  return escapeReservedForPortaledDatesPanel.has(event);
}

function dialogHostsDatesInput(dialog: HTMLElement): boolean {
  return Boolean(dialog.querySelector('[data-dates-input], .mantine-DateInput-input'));
}

function isInteractionWithExpandedControl(
  control: Element,
  owned: HTMLElement | null,
  interactionTarget: EventTarget | null,
): boolean {
  const focus =
    interactionTarget instanceof Element
      ? interactionTarget
      : typeof document !== 'undefined'
        ? document.activeElement
        : null;
  if (!(focus instanceof Element)) {
    return false;
  }
  if (control === focus || control.contains(focus)) {
    return true;
  }
  if (owned && (owned === focus || owned.contains(focus))) {
    return true;
  }
  return false;
}

/**
 * Expanded pickers inside the top dialog only — never page-level combobox chrome
 * behind a modal (Gate #1432 / contacts Escape regressions).
 */
function hasExpandedInlinePickerInDialog(
  dialog: HTMLElement,
  interactionTarget: EventTarget | null = null,
): boolean {
  for (const element of dialog.querySelectorAll('[aria-expanded="true"]')) {
    if (!(element instanceof Element) || !isVisibleOverlayElement(element)) {
      continue;
    }
    if (element.closest('[data-dates-input], .mantine-DateInput-input')) {
      if (!hasVisiblePortaledDatesPanel()) {
        continue;
      }
      if (!isInteractionWithExpandedControl(element, null, interactionTarget)) {
        continue;
      }
      return true;
    }
    if (element.getAttribute('role') === 'combobox') {
      const controlsId = element.getAttribute('aria-controls');
      if (controlsId) {
        const owned = document.getElementById(controlsId);
        if (
          owned instanceof HTMLElement
          && isVisibleOverlayElement(owned)
          && isInteractionWithExpandedControl(element, owned, interactionTarget)
        ) {
          return true;
        }
      }
      continue;
    }
    if (element.getAttribute('aria-haspopup') === 'listbox') {
      const controlsId = element.getAttribute('aria-controls');
      if (controlsId) {
        const owned = document.getElementById(controlsId);
        if (
          owned instanceof HTMLElement
          && isVisibleOverlayElement(owned)
          && isInteractionWithExpandedControl(element, owned, interactionTarget)
        ) {
          return true;
        }
      }
      continue;
    }
  }
  return false;
}

function hasVisibleOverlayInPopoverHost(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const popover = target.closest('.mantine-Popover-dropdown');
  if (!(popover instanceof HTMLElement)) {
    return false;
  }
  for (const selector of ['[data-dates-dropdown]', '[data-dates-modal]', '[data-combobox-dropdown]']) {
    for (const element of popover.querySelectorAll(selector)) {
      if (isVisibleOverlayElement(element)) {
        return true;
      }
    }
  }
  return false;
}

function shouldDeferForPortaledDatesPicker(
  topDialog: HTMLElement | null,
  target: EventTarget | null,
): boolean {
  if (!topDialog || !dialogHostsDatesInput(topDialog) || !hasVisiblePortaledDatesPanel()) {
    return false;
  }
  // Mantine paints the calendar in a portal; defer only while that layer is the active
  // Escape target — not when focus is on footer CTAs (#873) or a stale portal remains (#1432).
  if (target instanceof Element) {
    if (target.closest('[data-dates-dropdown], [data-dates-modal]')) {
      return true;
    }
    const dateControl = target.closest('[data-dates-input], .mantine-DateInput-input');
    if (dateControl instanceof HTMLElement && topDialog.contains(dateControl)) {
      return true;
    }
  }
  const active = document.activeElement;
  if (active instanceof Element) {
    if (active.closest('[data-dates-dropdown], [data-dates-modal]')) {
      return true;
    }
    const activeDate = active.closest('[data-dates-input], .mantine-DateInput-input');
    if (activeDate instanceof HTMLElement && topDialog.contains(activeDate)) {
      return true;
    }
  }
  return false;
}

/**
 * Mantine DateInput can keep aria-expanded true briefly after the portaled calendar closes.
 * Defer guarded close only while focus/target is still on that control (#1432), not footer CTAs.
 */
function shouldDeferForExpandedDateInputGhost(
  dialog: HTMLElement,
  target: EventTarget | null,
): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  if (getDialogAncestor(target) !== dialog) {
    return false;
  }
  const dateControl = target.closest('[data-dates-input], .mantine-DateInput-input');
  if (!(dateControl instanceof HTMLElement) || !dialog.contains(dateControl)) {
    return false;
  }
  if (hasVisiblePortaledDatesPanel()) {
    return true;
  }
  return dateControl.getAttribute('aria-expanded') === 'true';
}

export function hasOpenModalInnerOverlay(target: EventTarget | null): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const topDialog = getTopmostVisibleDialogForKeyboardEvent(target);
  if (shouldDeferForPortaledDatesPicker(topDialog, target)) {
    return true;
  }
  if (topDialog && shouldDeferForExpandedDateInputGhost(topDialog, target)) {
    return true;
  }
  if (topDialog && hasExpandedInlinePickerInDialog(topDialog, target)) {
    return true;
  }

  if (hasVisibleOverlayInPopoverHost(target)) {
    return true;
  }

  if (target instanceof Element) {
    const targetDialog = getDialogAncestor(target);
    if (
      target.closest('[data-dates-input]')
      && hasVisiblePortaledDatesPanel()
      && targetDialog
      && dialogHostsDatesInput(targetDialog)
    ) {
      return true;
    }
    const combobox = target.closest('[role="combobox"]');
    if (combobox instanceof HTMLElement && combobox.getAttribute('aria-expanded') === 'true') {
      const controlsId = combobox.getAttribute('aria-controls');
      if (controlsId) {
        const owned = document.getElementById(controlsId);
        if (owned instanceof HTMLElement && isVisibleOverlayElement(owned)) {
          return true;
        }
      }
      const targetDialog = getDialogAncestor(target);
      if (targetDialog) {
        for (const element of document.querySelectorAll('[data-combobox-dropdown], .mantine-Select-dropdown')) {
          if (!(element instanceof HTMLElement) || !isVisibleOverlayElement(element)) {
            continue;
          }
          const dropdownDialog = getDialogAncestor(element);
          if (dropdownDialog === targetDialog) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

export function getDialogAncestor(element: Element | null): HTMLElement | null {
  if (!element) {
    return null;
  }
  return element.closest('[role="dialog"]') as HTMLElement | null;
}

/** True when Escape should route to this container (focus or target inside its dialog). */
function shouldHandleEscapeForContainer(
  container: HTMLElement,
  eventTarget: EventTarget | null,
): boolean {
  const ownerDialog = getDialogAncestor(container);
  if (!ownerDialog) {
    return false;
  }
  const topDialog = getTopmostVisibleDialogForKeyboardEvent(eventTarget);
  if (topDialog !== ownerDialog) {
    return false;
  }
  if (eventTarget instanceof Node && ownerDialog.contains(eventTarget)) {
    return true;
  }
  const active = document.activeElement;
  if (active instanceof Node && ownerDialog.contains(active)) {
    return true;
  }
  // After "Keep editing" we blur the active control; focus often lands on <body>.
  if (active instanceof HTMLElement && (active.tagName === 'BODY' || active.tagName === 'HTML')) {
    return true;
  }
  return false;
}

/**
 * True when the modal form/wizard shell should ignore Escape (not top dialog).
 */
export function shouldIgnoreModalEscapeForContainer(
  container: HTMLElement,
  eventTarget: EventTarget | null,
): boolean {
  const ownerDialog = getDialogAncestor(container);
  const topDialog = getTopmostVisibleDialogForKeyboardEvent(eventTarget);
  if (!ownerDialog || !topDialog) {
    return true;
  }
  if (ownerDialog !== topDialog) {
    return true;
  }
  if (!(eventTarget instanceof Element)) {
    return false;
  }
  const targetDialog = getDialogAncestor(eventTarget);
  return targetDialog !== null && targetDialog !== ownerDialog;
}

/** Prefer focused control over BODY/HTML when Playwright or the browser dispatches Escape. */
function getKeyboardInteractionTarget(event: KeyboardEvent): EventTarget | null {
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    const target = event.target;
    if (
      !(target instanceof Element)
      || target.tagName === 'BODY'
      || target.tagName === 'HTML'
    ) {
      return active;
    }
  }
  return event.target;
}

/**
 * Native keyboard Escape deferral (overlays open inside the top dialog).
 */
export function shouldDeferModalFormEscapeFromKeyboardEvent(
  event: KeyboardEvent,
): boolean {
  if (event.key !== 'Escape') {
    return false;
  }
  const interactionTarget = getKeyboardInteractionTarget(event);
  if (
    interactionTarget instanceof HTMLButtonElement
    && interactionTarget.closest('[role="dialog"]')
    && interactionTarget.getAttribute('aria-expanded') !== 'true'
  ) {
    return false;
  }
  const ownerDialog = interactionTarget instanceof Element
    ? interactionTarget.closest('[role="dialog"]')
    : null;
  if (
    ownerDialog
    && Array.from(ownerDialog.querySelectorAll('.mantine-Popover-dropdown')).some((element) =>
      isVisibleOverlayElement(element),
    )
  ) {
    return true;
  }
  return hasOpenModalInnerOverlay(interactionTarget);
}

/**
 * Capture-phase Escape routing for modal forms/wizards (#1432).
 */
export function handleModalEscapeAtDocumentCapture(
  event: KeyboardEvent,
  container: HTMLElement,
  onRequestClose: () => void,
): void {
  if (event.key !== 'Escape' || event.defaultPrevented) {
    return;
  }
  if (!shouldHandleEscapeForContainer(container, event.target)) {
    return;
  }
  if (shouldIgnoreModalEscapeForContainer(container, event.target)) {
    return;
  }
  if (shouldDeferModalFormEscapeFromKeyboardEvent(event)) {
    deferredModalFormEscapeEvents.add(event);
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  onRequestClose();
}

/**
 * Routes Escape to guarded modal close when focus is inside a modal form.
 * Defers when an inner overlay or stacked dialog is open (#1432).
 */
export function handleModalFormEscapeKeyDown(
  event: ReactKeyboardEvent<HTMLElement>,
  onRequestClose: () => void,
): void {
  if (event.key !== 'Escape' || event.defaultPrevented) {
    return;
  }
  if (shouldDeferModalFormEscape(event)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  onRequestClose();
}

export function handleModalFormEnterKeyDown(
  event: ReactKeyboardEvent<HTMLFormElement>,
  mode: ModalFormEnterKeyMode,
): void {
  if (!shouldPreventEnterFormSubmit(event, mode)) {
    return;
  }

  const target = event.target;
  event.preventDefault();

  // SmartDateInput commits typed US dates on Enter (capture on its shell). Blurring here
  // raced Mantine controlled value and left compact MMDDYYYY unformatted in wizards (#1432).
  if (isDateInputElement(target)) {
    return;
  }
}
